import { useEffect, useState } from 'react';
import { OAuth2AuthCodePkceClient } from 'oauth2-pkce';
import useAuthStore from '../store/useAuthStore';
import { fetchUserData, fetchUserRoles } from '../api/userService';
import { sendUrlToBackend } from '../api/nebUrlService';

// Get redirect URL dynamically
const getRedirectUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const redirectUriFromQuery = urlParams.get('redirect_uri'); 
    return redirectUriFromQuery || process.env.REACT_APP_OAUTH_REDIRECT_URI;
};

// OAuth client instance
const oauthClient = new OAuth2AuthCodePkceClient({
    scopes: process.env.REACT_APP_SCOPES.split(' '),
    authorizationUrl: process.env.REACT_APP_OAUTH_AUTHORIZATION_URL,
    tokenUrl: process.env.REACT_APP_OAUTH_TOKEN_URL,
    clientId: process.env.REACT_APP_OAUTH_CLIENT_ID,
    redirectUrl: getRedirectUrl(),
    storeRefreshToken: false,
    onAccessTokenExpiry() {
        return oauthClient.exchangeRefreshTokenForAccessToken();
    },
    onInvalidGrant() {
        console.warn('Invalid grant detected! Redirecting.');
        const url = new URL(window.location.href);
        url.search = ''; // Remove query params
        window.history.replaceState({}, document.title, url.toString());
    },
    onInvalidToken() {
        console.warn('Invalid token detected. Refresh needed.');
    },
});

export const useLiferayOAuth = (requiresAuth = true) => {
    const { token, login, logout: storeLogout, setRoles, setUser } = useAuthStore();
    const [loginInProgress, setLoginInProgress] = useState(true);

    // Logout function
    const oauthLogout = () => {
        console.log("Logging out...");
        storeLogout(); // Clear Zustand store

        const logoutUrl = process.env.REACT_APP_LOGOUT_URL;
        if (logoutUrl) {
            window.open(logoutUrl, "_self");
        } else {
            console.error('REACT_APP_LOGOUT_URL is not defined.');
        }
    };

    useEffect(() => {
        const performLogin = async () => {
            try {
                const currentUrl = window.location.href;
                console.log('Current URL:', currentUrl);

                // If token already exists in Zustand, skip re-authentication
                if (token) {
                    console.log('Token exists in Zustand. No need to re-authenticate.');
                    setLoginInProgress(false);
                    return;
                }

                const urlParams = new URLSearchParams(window.location.search);
                const authCode = urlParams.get('code');

                if (authCode) {
                    await oauthClient.receiveCode();
                    const tokens = await oauthClient.getTokens();
                    const accessTokenString = tokens.accessToken || '';

                    login({ name: 'Authenticated User' }, accessTokenString); // Store in Zustand

                    // Fetch and store user data
                    const userData = await fetchUserData(accessTokenString);
                    setUser(userData);
                    await sendUrlToBackend(currentUrl, userData.id);

                    // Fetch and store user roles
                    const roles = await fetchUserRoles(accessTokenString);
                    setRoles(roles);
                } else if (requiresAuth) {
                    console.log('Requesting OAuth authorization...');
                    await oauthClient.requestAuthorizationCode();
                }
            } catch (error) {
                console.error('Login failed:', error);
                storeLogout(); // Logout on failure
            } finally {
                setLoginInProgress(false);
            }
        };

        if (requiresAuth && !token) {
            performLogin();
        } else {
            setLoginInProgress(false);
        }
    }, [token, login, storeLogout, setRoles, setUser, requiresAuth]);

    return { token, loginInProgress, oauthLogout };
};